import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, PanelLeft as PlaceholderIcon } from 'lucide-react';
import clsx from 'clsx';
import styles from './HierarchicalAccordionItem.module.css';

type AccordionLevel = 1 | 2 | 3 | 'primary' | 'secondary' | 'tertiary';

interface HierarchicalAccordionItemProps {
  id: string | number;
  title: string;
  level: AccordionLevel;
  icon?: React.ElementType; // Added icon prop
  children: React.ReactNode;
  isOpen?: boolean; // Iniettata dal genitore HierarchicalAccordion
  onToggle?: (id: string | number) => void; // Iniettata dal genitore HierarchicalAccordion
}

const getTitleClassName = (level: AccordionLevel): string => {
  switch (level) {
    case 1:
    case 'primary':
      return styles.titlePrimary;
    case 2:
    case 'secondary':
      return styles.titleSecondary;
    case 3:
    case 'tertiary':
      return styles.titleTertiary;
    default:
      return styles.titlePrimary; // Fallback
  }
};

const HierarchicalAccordionItem: React.FC<HierarchicalAccordionItemProps> = ({
  id,
  title,
  level,
  icon, // Added icon
  children,
  isOpen = false,
  onToggle,
}) => {
  const handleHeaderClick = () => {
    if (onToggle) {
      onToggle(id);
    }
  };

  const titleClassName = getTitleClassName(level);

  const isPrimaryLevel = level === 1 || level === 'primary';
  const isSecondaryLevelWithFieldset = level === 2 || level === 'secondary';

  if (isSecondaryLevelWithFieldset) {
    return (
      <div className={styles.level2FieldsetWrapper}>
        <div
          className={clsx(
            styles.itemHeader,
            styles.level2TitleContainer,
            isOpen && styles.isOpen // Per .level2TitleContainer.isOpen .titleSecondary
          )}
          onClick={handleHeaderClick}
          role="button"
          aria-expanded={isOpen}
          aria-controls={`accordion-content-${id}`}
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleHeaderClick(); }}
        >
          <span className={titleClassName}>{title}</span>
        </div>
        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              key="content"
              id={`accordion-content-${id}`}
              className={clsx(styles.content, styles.level2ContentBox)}
              role="region"
              aria-labelledby={`accordion-header-${id}`}
              initial="collapsed"
              animate="open"
              exit="collapsed"
              variants={{
                open: { opacity: 1, height: 'auto' },
                collapsed: { opacity: 0, height: 0 }
              }}
              transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
            >
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Struttura per livelli 1, 3 e altri eventuali fallback
  const itemWrapperClasses = clsx(
    styles.accordionItem,
    {
      [styles.level1]: isPrimaryLevel,
      [styles.level2]: isSecondaryLevelWithFieldset, // Anche se questo blocco non dovrebbe essere raggiunto se isSecondaryLevelWithFieldset è true qui
      [styles.level3]: level === 3 || level === 'tertiary',
    }
  );

  return (
    <div className={itemWrapperClasses}>
      <div
        className={clsx(
          styles.itemHeader,
          {
            [styles.itemHeaderLevel1]: isPrimaryLevel,
            [styles.itemHeaderNested]: !isPrimaryLevel,
            [styles.isOpen]: isOpen && isPrimaryLevel, // Per .itemHeaderLevel1.isOpen
          }
        )}
        onClick={handleHeaderClick}
        role="button"
        aria-expanded={isOpen}
        aria-controls={`accordion-content-${id}`}
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleHeaderClick(); }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {isPrimaryLevel && (
            icon ? React.createElement(icon, { size: 16, style: { marginRight: 'var(--spacing-s)' } })
            : <PlaceholderIcon size={16} style={{ marginRight: 'var(--spacing-s)' }} />
          )}
          <span className={titleClassName}>
            {title}
          </span>
        </div>
        {(isPrimaryLevel || !isSecondaryLevelWithFieldset) && (
          isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />
        )}
      </div>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="content"
            id={`accordion-content-${id}`}
            className={styles.content}
            role="region"
            aria-labelledby={`accordion-header-${id}`}
            aria-hidden={!isOpen}
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: { opacity: 1, height: 'auto' },
              collapsed: { opacity: 0, height: 0 }
            }}
            transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HierarchicalAccordionItem;